import {type FC, useEffect} from "react";

import {toast, ToastContainer, type ToastOptions} from "react-toastify";

import {useAppSelector} from "../../hooks";
import type {RootState} from "../../store";
import {Localization} from "../../utils/Localization";

const toastOptions: ToastOptions = {
  position: 'top-right',
  // close automatically in 10 seconds
  autoClose: 10_000,
}

/**
 * Wrapper component for notification library the app is utilizing.
 * Note: notifications component should be included only once in the DOM.
 */
export const Notifications: FC = () => <ToastContainer/>

interface NotificationAPI {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warn: (message: string) => void;
}

const notificationsAPI: Readonly<NotificationAPI> = {
  success: message => toast.success(message, toastOptions),
  error: message => toast.error(message, toastOptions),
  info: message => toast.info(message, toastOptions),
  warn: message => toast.warn(message, toastOptions),
}

/**
 * Helper API to use app notifications.
 */
export function useNotifications(): NotificationAPI {
  return notificationsAPI
}

/* Helper Hooks */

/**
 * Helper hook that shows notification whenever error field in specific state object is updated.
 * @param errorStateResolver
 */
export function useErrorNotifications(errorStateResolver: (state: RootState) => string | null) {
  const error = useAppSelector<string | null>(errorStateResolver);

  useEffect(() => {
    if (error) {
      const title = Localization.getLocalizedNotificationTitle('error')
      const msg = `${title}${error}`
      notificationsAPI.error(msg)
    }
  }, [error]);
}
